import { type ComponentProps, type FC, useMemo, useState } from "react";

import { Checkbox } from "@snapchat/snap-design-system";

import { MetricField } from "../../../gql-generated/types";
import { getKeys } from "../../../shared/utils/object";
import { useInsightsPageBlizzardEvent } from "../../hooks";
import { ComputedMetricField, type InsightsMetricsMap, type StatsItem } from "../../models";

import { FilterRow, filter } from "./EventInsightsTab.styles";
import { EventMetric } from "./EventMetric";
import { MetricFilter } from "./MetricFilter";
import { EventInsightMetric, type Metrics, defaultMetrics } from "./constants";
import { CompareTotalPlays, messages } from "./messages.generated";

interface EventInsightsTab {
    chartMetrics: InsightsMetricsMap<StatsItem>;
    chartMetricsComputed: InsightsMetricsMap<StatsItem>;
    loading: boolean;
}

export const EventInsightsTab: FC<EventInsightsTab> = ({ chartMetrics, chartMetricsComputed, loading }) => {
    useInsightsPageBlizzardEvent("MY_LENSES_INSIGHTS_EVENT_INSIGHTS");

    const [compareWithTotalPlays, setCompareWithTotalPlays] = useState(false);
    const [metrics, setMetrics] = useState<Metrics>(defaultMetrics);

    const defaultMetric: ComponentProps<typeof EventMetric> = useMemo(
        () => ({
            label: messages.defaultMetricLabel,
            baseMetric: MetricField.LensViews,
            chartMetrics,
            chartMetricsComputed,
            compareWithTotalPlays,
            loading,
        }),
        [chartMetrics, chartMetricsComputed, compareWithTotalPlays, loading],
    );

    const metricsMap: { [key in EventInsightMetric]: ComponentProps<typeof EventMetric> } = useMemo(
        () => ({
            [EventInsightMetric.LensFaceFound]: {
                ...defaultMetric,
                label: messages.faceFoundLabel,
                labelAverage: messages.faceFoundLabelAverage,
                tooltip: messages.faceFoundTooltip,
                tooltipAverage: messages.faceFoundTooltipAverage,
                baseMetric: MetricField.LensFaceFound,
                averageMetric: ComputedMetricField.AverageLensFaceFound,
            },
            [EventInsightMetric.LensFaceLost]: {
                ...defaultMetric,
                label: messages.faceLostLabel,
                labelAverage: messages.faceLostLabelAverage,
                tooltip: messages.faceLostTooltip,
                tooltipAverage: messages.faceLostTooltipAverage,
                baseMetric: MetricField.LensFaceLost,
                averageMetric: ComputedMetricField.AverageLensFaceLost,
            },
            [EventInsightMetric.LensObjectTracked]: {
                ...defaultMetric,
                label: messages.objectTrackedLabel,
                labelAverage: messages.objectTrackedLabelAverage,
                tooltip: messages.objectTrackedTooltip,
                tooltipAverage: messages.objectTrackedTooltipAverage,
                baseMetric: MetricField.LensObjectTracked,
                averageMetric: ComputedMetricField.AverageLensObjectTracked,
            },
            [EventInsightMetric.LensObjectTracked_3D]: {
                ...defaultMetric,
                label: messages.objectTracked3dLabel,
                labelAverage: messages.objectTracked3dLabelAverage,
                tooltip: messages.objectTracked3dTooltip,
                tooltipAverage: messages.objectTracked3dTooltipAverage,
                baseMetric: MetricField.LensObjectTracked_3D,
                averageMetric: ComputedMetricField.AverageLensObjectTracked_3D,
            },
            [EventInsightMetric.LensMarkerTracked]: {
                ...defaultMetric,
                label: messages.markerTrackedLabel,
                labelAverage: messages.markerTrackedLabelAverage,
                tooltip: messages.markerTrackedTooltip,
                tooltipAverage: messages.markerTrackedTooltipAverage,
                baseMetric: MetricField.LensMarkerTracked,
                averageMetric: ComputedMetricField.AverageLensMarkerTracked,
            },
            [EventInsightMetric.LensTap]: {
                ...defaultMetric,
                label: messages.tapLabel,
                labelAverage: messages.tapLabelAverage,
                tooltip: messages.tapTooltip,
                tooltipAverage: messages.tapTooltipAverage,
                baseMetric: MetricField.LensTap,
                averageMetric: ComputedMetricField.AverageLensTap,
            },
            [EventInsightMetric.LensMultiuserSessionShared]: {
                ...defaultMetric,
                label: messages.multiuserSessionSharedLabel,
                tooltip: messages.multiuserSessionSharedTooltip,
                baseMetric: MetricField.LensMultiuserSessionShared,
            },
            [EventInsightMetric.LensMultiuserSessionJoined]: {
                ...defaultMetric,
                label: messages.multiuserSessionJoinedLabel,
                tooltip: messages.multiuserSessionJoinedTooltip,
                baseMetric: MetricField.LensMultiuserSessionJoined,
            },
        }),
        [defaultMetric],
    );

    const activeMetricProps = useMemo(() => {
        return getKeys(metrics)
            .filter((activeMetric: EventInsightMetric) => metrics[activeMetric].isActive)
            .map((activeMetric: EventInsightMetric) => metricsMap[activeMetric]);
    }, [metrics, metricsMap]);

    return (
        <>
            <FilterRow>
                <Checkbox
                    containerClassName={filter}
                    label={<CompareTotalPlays />}
                    value={compareWithTotalPlays}
                    onChange={setCompareWithTotalPlays}
                />
                <MetricFilter metrics={metrics} onFilterChange={setMetrics} />
            </FilterRow>
            {activeMetricProps.map((metricProps) => (
                <EventMetric key={metricProps.baseMetric} {...metricProps} />
            ))}
        </>
    );
};
